import React, { useEffect, useState } from 'react';
import { Button, Result, Spin } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { makeGetCall, getUserDetails } from '../utils/helper';

// Embeds a centre's HIS lab-results portal (rad_centres.his_lab_url).
// Centre resolution: `centerCode` (the order's po_center_code) wins;
// otherwise the user's primary_center_code.
// `pin` (the patient's pat_pin) is appended as ?pin=<pin>.
interface LabResultsProps {
  pin?: string;
  centerCode?: string;
}

const buildPortalUrl = (base: string, pin?: string) => {
  try {
    const url = new URL(base);
    if (pin) url.searchParams.set('pin', pin);
    return url.toString();
  } catch {
    return base;
  }
};

const LabResults: React.FC<LabResultsProps> = ({ pin, centerCode }) => {
  const [loading, setLoading] = useState(true);
  const [labUrl, setLabUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchLabUrl = async () => {
      setLoading(true);
      try {
        const res = await makeGetCall('/centres');
        const centres = res.data?.data || [];
        const norm = (v?: string) => (v || '').trim().toLowerCase();

        let centre = centerCode
          ? centres.find(c => norm(c.code) === norm(centerCode))
          : null;
        if (!centre) {
          const primary = getUserDetails()?.primary_center_code;
          centre = primary ? centres.find(c => norm(c.code) === norm(primary)) : null;
        }
        setLabUrl(centre?.his_lab_url || null);
      } catch (error) {
        console.error('Failed to fetch centre lab URL:', error);
        setLabUrl(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLabUrl();
  }, [pin, centerCode]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin tip="Loading lab results portal..." />
      </div>
    );
  }

  if (!labUrl) {
    return (
      <Result
        status="info"
        title="Lab results portal is not configured for your centre"
        subTitle="Ask an administrator to set the HIS Lab URL for your centre in Settings → Centers."
      />
    );
  }

  const portalUrl = buildPortalUrl(labUrl, pin);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* X-Frame-Options refusal isn't detectable cross-origin, so the
          open-in-new-tab escape hatch is always available in the toolbar. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 10px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fafafa',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 12, color: '#999' }}>
          If the portal appears blank, it may not allow embedding — open it in a new tab.
        </span>
        <Button
          size="small"
          icon={<ExportOutlined />}
          onClick={() => window.open(portalUrl, '_blank', 'noopener')}
        >
          Open in new tab
        </Button>
      </div>
      <iframe
        src={portalUrl}
        title="Lab Results"
        style={{ flex: 1, width: '100%', border: 'none' }}
      />
    </div>
  );
};

export default LabResults;
